/**
 * Content Metadata Dialog
 *
 * Reusable dialog for editing metadata on any content item (files or views).
 * Patterned on ViewMetadataDialog — same state shape, validation rules, tag
 * loading strategy, and MUI component set. Caller chooses which status values
 * to expose via the `availableStatuses` prop.
 *
 * Task 6.1 + 6.2: Added AI suggestions, polling, and Regenerate affordances
 * matching EditContentDialog. AI section is rendered only when `contentId` is
 * provided; legacy callers (e.g. EnhancedPageEditor) that omit it continue to
 * work unchanged.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Public as PublicIcon,
  Lock as PrivateIcon,
  Drafts as DraftIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ImageIcon from '@mui/icons-material/Image';
import TagAutoComplete from '../common/TagAutoComplete';
import WYSIWYGRichTextEditor from '../page-builder/WYSIWYGRichTextEditor';
import { tagService, contentService } from '../../services';
import type { UnifiedTag, CreateTagRequest } from '../../services';
import type { TagScope } from '../../types/content.types';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ContentMetadataValues {
  title: string;
  description: string;
  tagIds: string[];
  status?: 'draft' | 'private' | 'public' | 'archived';
  /** View category — passed through for views, ignored for file-based content */
  category?: string;
  thumbnailUrl?: string;
  /** Legacy flag still expected by ViewMetadata */
  isPublic?: boolean;
  teamId?: string;
  organizationId?: string;
  /** Read-only fields surfaced from the content row's enrichment.
   * Used by the dialog to seed aiSuggestions on open. */
  aiDescription?: string;
  aiSuggestedTitle?: string;
  suggestedTags?: Array<{ tagName: string; confidence?: number } | string>;
}

export interface ContentMetadataDialogProps {
  open: boolean;
  initialValues: ContentMetadataValues;
  onSave: (values: ContentMetadataValues) => Promise<void> | void;
  onClose: () => void;
  /** Content kind for polling + Regenerate dispatch. Defaults to 'view'
   * (Plan A baseline — view-designer was the only original caller). */
  kind?: 'file' | 'view';
  /** Content ID — required for AI affordances. If absent, the AI section
   * is hidden so legacy callers that don't pass it continue to work. */
  contentId?: string;
  /** Fires when Regenerate is clicked (before polling). */
  onEnrichmentTriggered?: (contentId: string) => void;
  /** Fires when polling detects enrichment is complete. */
  onEnrichmentComplete?: (contentId: string) => void;
  /**
   * Which status options to render in the status selector.
   * Defaults to ['draft', 'private', 'archived'] — the current view-designer
   * behaviour where 'public' is intentionally hidden.
   * File callers (Plan C) should pass ['draft', 'private', 'public', 'archived'].
   */
  availableStatuses?: Array<'draft' | 'private' | 'public' | 'archived'>;
}

// ---------------------------------------------------------------------------
// AI-polling constants (match EditContentDialog)
// ---------------------------------------------------------------------------

interface AiSuggestions {
  title: string | null;
  description: string | null;
  tags: string[];
}

const POLL_INTERVAL_MS = 5_000;
const POLL_TIMEOUT_MS = 180_000;
/** Processing states where file-based enrichment is still in flight. */
const AI_IN_PROGRESS_STATES = new Set([
  'uploaded',
  'metadata_extracted',
  'text_extracted',
  'ai_enrichment_pending',
]);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const DEFAULT_STATUSES: Array<'draft' | 'private' | 'public' | 'archived'> = [
  'draft',
  'private',
  'archived',
];

/** Resolve the tag name string from a suggestedTags entry (object or plain string). */
function resolveTagName(entry: { tagName: string; confidence?: number } | string): string {
  return typeof entry === 'string' ? entry : entry.tagName;
}

export function ContentMetadataDialog({
  open,
  initialValues,
  onSave,
  onClose,
  kind = 'view',
  contentId,
  onEnrichmentTriggered,
  onEnrichmentComplete,
  availableStatuses = DEFAULT_STATUSES,
}: ContentMetadataDialogProps) {
  const [values, setValues] = useState<ContentMetadataValues>({
    title: initialValues.title ?? '',
    description: initialValues.description ?? '',
    tagIds: initialValues.tagIds ?? [],
    status: initialValues.status ?? 'private',
    category: initialValues.category,
    thumbnailUrl: initialValues.thumbnailUrl,
    isPublic: initialValues.isPublic ?? false,
    teamId: initialValues.teamId,
    organizationId: initialValues.organizationId,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [availableTags, setAvailableTags] = useState<UnifiedTag[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);

  // ── AI suggestion state ────────────────────────────────────────────────────
  const [aiSuggestions, setAiSuggestions] = useState<AiSuggestions>({
    title: null,
    description: null,
    tags: [],
  });
  const [descExpanded, setDescExpanded] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);
  const [regenerateSuccess, setRegenerateSuccess] = useState(false);
  const [regeneratingThumbnail, setRegeneratingThumbnail] = useState(false);
  const [thumbnailError, setThumbnailError] = useState<string | null>(null);
  const [thumbnailQueued, setThumbnailQueued] = useState(false);
  const [pollTimedOut, setPollTimedOut] = useState(false);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Ref so the initialization useEffect can read regenerating without it being a dep.
  const regeneratingRef = useRef(regenerating);
  regeneratingRef.current = regenerating;

  // Guard: prevent the open/initialValues sync useEffect from overwriting
  // freshly-polled suggestions when onEnrichmentComplete triggers a parent
  // re-render that propagates updated initialValues.
  const regenerateSuccessRef = useRef(regenerateSuccess);
  regenerateSuccessRef.current = regenerateSuccess;

  // Separate from the enrichment poll's refs on purpose: both regenerations are
  // reachable from this dialog, and sharing one timer pair would let whichever
  // finished first cancel the other's polling.
  const thumbPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const thumbPollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopThumbnailPolling = useCallback(() => {
    if (thumbPollIntervalRef.current) {
      clearInterval(thumbPollIntervalRef.current);
      thumbPollIntervalRef.current = null;
    }
    if (thumbPollTimeoutRef.current) {
      clearTimeout(thumbPollTimeoutRef.current);
      thumbPollTimeoutRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => () => stopPolling(), [stopPolling]);
  useEffect(() => () => stopThumbnailPolling(), [stopThumbnailPolling]);

  // Re-sync internal state when the dialog is opened with new initialValues.
  // Skipped while regenerating (active enrichment) or just after completion
  // (polling already set aiSuggestions; parent re-render should not overwrite them).
  useEffect(() => {
    if (!open) return;
    if (regeneratingRef.current) return;
    if (regenerateSuccessRef.current) return;

    setValues({
      title: initialValues.title ?? '',
      description: initialValues.description ?? '',
      tagIds: initialValues.tagIds ?? [],
      status: initialValues.status ?? 'private',
      category: initialValues.category,
      thumbnailUrl: initialValues.thumbnailUrl,
      isPublic: initialValues.isPublic ?? false,
      teamId: initialValues.teamId,
      organizationId: initialValues.organizationId,
    });
    setErrors({});

    // Seed AI suggestions from enrichment fields in initialValues — these
    // may be missing (e.g. view editor's metadata state doesn't carry AI
    // fields because the View proto has no AI fields). Fall back to a
    // fresh contentService.getContent fetch below for the missing case.
    if (contentId) {
      const seededTitle = initialValues.aiSuggestedTitle || null;
      const seededDescription = initialValues.aiDescription || null;
      const seededTags = (initialValues.suggestedTags ?? []).map(resolveTagName);
      setAiSuggestions({
        title: seededTitle,
        description: seededDescription,
        tags: seededTags,
      });

      // If the caller didn't pass AI fields via initialValues, fetch them
      // directly. The view designer falls into this case because the View
      // proto doesn't carry aiDescription/aiSuggestedTitle/suggestedTags.
      const haveAiData = !!(seededTitle || seededDescription || seededTags.length > 0);
      if (!haveAiData) {
        const fetchTeamId = initialValues.teamId ?? '';
        contentService.getContent(contentId, fetchTeamId, true).then((fresh: any) => {
          if (regeneratingRef.current || regenerateSuccessRef.current) return;
          const c = fresh?.content ?? fresh;
          if (!c) return;
          setAiSuggestions({
            title: c.aiSuggestedTitle || null,
            description: c.aiDescription || null,
            tags: (c.suggestedTags ?? []).map(resolveTagName),
          });
        }).catch(() => {
          // Non-fatal — dialog still works without AI suggestions.
        });
      }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load available tags whenever the dialog opens and org/team context is present.
  useEffect(() => {
    if (open && values.teamId && values.organizationId) {
      loadTags(values.teamId, values.organizationId);
    }
  }, [open, values.teamId, values.organizationId]);

  const loadTags = async (teamId: string, organizationId: string) => {
    try {
      setLoadingTags(true);
      const tags = await tagService.listUnifiedTags({
        organization_id: organizationId,
        team_id: teamId,
        scope: 'team',
      });
      setAvailableTags(tags);
    } catch (error) {
      console.error('Failed to load tags:', error);
      setErrors(prev => ({ ...prev, tags: 'Failed to load tags' }));
    } finally {
      setLoadingTags(false);
    }
  };

  // -------------------------------------------------------------------------
  // Tag handlers
  // -------------------------------------------------------------------------

  const handleTagApply = (tagId: string, tag: UnifiedTag) => {
    setValues(prev => ({ ...prev, tagIds: [...prev.tagIds, tagId] }));
    if (!availableTags.find(t => t.id === tagId)) {
      setAvailableTags(prev => [...prev, tag]);
    }
  };

  const handleTagRemove = (tagId: string) => {
    setValues(prev => ({
      ...prev,
      tagIds: prev.tagIds.filter(id => id !== tagId),
    }));
  };

  const handleCreateTag = async (request: CreateTagRequest): Promise<UnifiedTag> => {
    try {
      const newTag = await tagService.createUnifiedTag(request);
      return newTag;
    } catch (error) {
      console.error('Failed to create tag:', error);
      throw error;
    }
  };

  // -------------------------------------------------------------------------
  // Field change
  // -------------------------------------------------------------------------

  const handleChange =
    (field: keyof ContentMetadataValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any) => {
      const value =
        event.target.type === 'checkbox' ? event.target.checked : event.target.value;
      setValues(prev => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: '' }));
      }
    };

  const handleDescriptionChange = (value: string) => {
    setValues(prev => ({ ...prev, description: value }));
    if (errors.description) {
      setErrors(prev => ({ ...prev, description: '' }));
    }
  };

  // -------------------------------------------------------------------------
  // Validation
  // -------------------------------------------------------------------------

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const title = (values.title ?? '').trim();
    const description = (values.description ?? '').trim();

    if (!title) {
      newErrors.title = 'Title is required';
    } else if (title.length < 3) {
      newErrors.title = 'Title must be at least 3 characters';
    } else if (title.length > 100) {
      newErrors.title = 'Title must be less than 100 characters';
    }

    if (!description) {
      newErrors.description = 'Description is required';
    } else if (description.length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    } else if (description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    if ((values.tagIds ?? []).length > 10) {
      newErrors.tagIds = 'Maximum of 10 tags allowed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // -------------------------------------------------------------------------
  // Save / close
  // -------------------------------------------------------------------------

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      await onSave({
        ...values,
        title: values.title.trim(),
        description: values.description.trim(),
      });
      onClose();
    } catch (error) {
      console.error('Failed to save metadata:', error);
      setErrors({ general: 'Failed to save. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isSaving) return;
    stopPolling();
    setRegenerating(false);
    setErrors({});
    onClose();
  };

  // -------------------------------------------------------------------------
  // AI enrichment: polling
  // -------------------------------------------------------------------------

  const pollForEnrichmentComplete = useCallback(
    (cid: string, teamId: string) => {
      pollTimeoutRef.current = setTimeout(() => {
        stopPolling();
        setRegenerating(false);
        setPollTimedOut(true);
      }, POLL_TIMEOUT_MS);

      pollIntervalRef.current = setInterval(async () => {
        try {
          const response = await contentService.getContent(cid, teamId, true);
          const fresh = response?.content ?? response;
          if (!fresh) return;

          // Completion signal is kind-aware:
          // - file: processingState leaves AI_IN_PROGRESS_STATES (same as EditContentDialog)
          // - view: aiDescription becomes non-empty (embeddingVector is select:false in Mongoose
          //   and not returned by default; aiDescription is the downstream signal)
          const done =
            kind === 'file'
              ? !AI_IN_PROGRESS_STATES.has(fresh.processingState ?? '')
              : !!(fresh.aiDescription || fresh.aiSuggestedTitle);

          if (done) {
            stopPolling();
            setAiSuggestions({
              title: fresh.aiSuggestedTitle || null,
              description: fresh.aiDescription || null,
              tags: (fresh.suggestedTags ?? []).map(resolveTagName),
            });
            setDescExpanded(false);
            setRegenerating(false);
            setRegenerateSuccess(true);
            onEnrichmentComplete?.(cid);
          }
        } catch {
          // network blip — keep polling
        }
      }, POLL_INTERVAL_MS);
    },
    [kind, stopPolling, onEnrichmentComplete],
  );

  // -------------------------------------------------------------------------
  // Thumbnail: Regenerate handler
  // -------------------------------------------------------------------------
  //
  // Unlike handleRegenerateEnrichment below, this deliberately does NOT save
  // the form first: regenerating a preview image derives nothing from the
  // metadata being edited, and saving as a side effect of a thumbnail fix
  // would commit edits the user had not chosen to commit yet.

  /**
   * Why a regeneration produced no image, in the user's terms.
   *
   * The reasons are deliberately not collapsed into one message: a missing
   * embedded preview is a fact about the file and nothing will change it, while
   * a failure is a fact about the service and is worth retrying. Telling a user
   * to retry something that cannot succeed — or not to retry something that
   * would — is the whole cost of conflating them.
   */
  const thumbnailUnavailableMessage = (reason?: string): string => {
    switch (reason) {
      case 'no_embedded_preview':
        return 'This file has no preview image inside it, and it is too large to render one. Re-saving it from PowerPoint with "Save preview picture" enabled will let this work.';
      case 'extraction_failed':
        return 'Could not read the file to build a thumbnail. This is usually temporary — try again.';
      case 'queue_failed':
        return 'Could not start thumbnail processing. Try again in a moment.';
      default:
        return 'No thumbnail could be generated for this item.';
    }
  };

  /**
   * Wait for the item's thumbnailState to leave 'regenerating'.
   *
   * The request is answered as soon as the work is accepted, so the response
   * carries no verdict — it used to, by doing the whole job inline, which made
   * its latency proportional to file size and pushed large files past the
   * client's 10s timeout. Polling the item is what replaces that.
   */
  const pollForThumbnailOutcome = useCallback(
    (cid: string, teamId: string) => {
      thumbPollTimeoutRef.current = setTimeout(() => {
        stopThumbnailPolling();
        setRegeneratingThumbnail(false);
        // Not an error: the work is still running server-side. Saying "failed"
        // here is exactly what the old timeout did, for work that then
        // succeeded.
        setThumbnailQueued(true);
      }, POLL_TIMEOUT_MS);

      thumbPollIntervalRef.current = setInterval(async () => {
        try {
          const response = await contentService.getContent(cid, teamId, true);
          const fresh = response?.content ?? response;
          if (!fresh || fresh.thumbnailState === 'regenerating') return;

          stopThumbnailPolling();
          setRegeneratingThumbnail(false);

          if (fresh.thumbnailState === 'ready') {
            setThumbnailQueued(true);
            onEnrichmentComplete?.(cid);
          } else if (fresh.thumbnailState === 'unavailable') {
            setThumbnailError(thumbnailUnavailableMessage(fresh.thumbnailStateReason));
          } else {
            // No state at all. Older items predate the field; the work may still
            // have succeeded, so do not claim it failed.
            setThumbnailQueued(true);
          }
        } catch {
          // network blip — keep polling
        }
      }, POLL_INTERVAL_MS);
    },
    [stopThumbnailPolling, onEnrichmentComplete],
  );

  const handleRegenerateThumbnail = async () => {
    if (!contentId) return;

    setRegeneratingThumbnail(true);
    setThumbnailError(null);
    setThumbnailQueued(false);

    try {
      const result = await contentService.regenerateThumbnail(contentId, values.teamId ?? '');

      // 202 means accepted, and only that. The outcome arrives on the item, so
      // keep the spinner up and wait for it rather than reporting a result the
      // server has not reached yet.
      if (result?.queued > 0) {
        pollForThumbnailOutcome(contentId, values.teamId ?? '');
      } else {
        setRegeneratingThumbnail(false);
        setThumbnailError(result?.message || thumbnailUnavailableMessage());
      }
    } catch (err: any) {
      setRegeneratingThumbnail(false);
      setThumbnailError(err?.message || 'Failed to regenerate thumbnail. Please try again.');
    }
  };

  // -------------------------------------------------------------------------
  // AI enrichment: Regenerate handler
  // -------------------------------------------------------------------------

  const handleRegenerateEnrichment = async () => {
    if (!contentId || !(values.title ?? '').trim()) return;

    setRegenerating(true);
    setRegenerateError(null);
    setRegenerateSuccess(false);
    setPollTimedOut(false);

    try {
      // Save current edits first so enrichment uses the latest metadata.
      // We bypass onSave->onClose by calling onSave directly (not handleSave).
      await onSave({
        ...values,
        title: values.title.trim(),
        description: values.description.trim(),
      });

      const teamId = values.teamId ?? '';
      await contentService.retryProcessing(contentId, teamId);
      onEnrichmentTriggered?.(contentId);

      pollForEnrichmentComplete(contentId, teamId);
    } catch (err: any) {
      setRegenerateError(err?.message || 'Failed to trigger AI enrichment. Please try again.');
      setRegenerating(false);
    }
  };

  // -------------------------------------------------------------------------
  // AI suggestion apply / dismiss
  // -------------------------------------------------------------------------

  const handleApplyTitle = () => {
    if (aiSuggestions.title) {
      setValues(prev => ({ ...prev, title: aiSuggestions.title! }));
      setAiSuggestions(prev => ({ ...prev, title: null }));
    }
  };

  const handleDismissTitle = () => {
    setAiSuggestions(prev => ({ ...prev, title: null }));
  };

  const handleApplyDescription = () => {
    if (aiSuggestions.description) {
      setValues(prev => ({ ...prev, description: aiSuggestions.description! }));
      setAiSuggestions(prev => ({ ...prev, description: null }));
    }
  };

  const handleDismissDescription = () => {
    setAiSuggestions(prev => ({ ...prev, description: null }));
  };

  const handleApplyTags = async () => {
    // Look up each suggested name in the loaded tag list; create it if absent.
    // Uses the dialog's internal availableTags state (same org/team context).
    const suggestedNames = aiSuggestions.tags.map(n => n.toLowerCase());
    const toApply: UnifiedTag[] = [];

    for (const name of aiSuggestions.tags) {
      const existing = availableTags.find(
        t => t.name.toLowerCase() === name.toLowerCase(),
      );
      if (existing) {
        if (!values.tagIds.includes(existing.id)) {
          toApply.push(existing);
        }
      } else if (values.organizationId && values.teamId) {
        // Tag doesn't exist yet — create it.
        try {
          const created = await tagService.createUnifiedTag({
            name,
            scope: 'team' as TagScope,
            organization_id: values.organizationId,
            team_id: values.teamId,
          } as CreateTagRequest);
          toApply.push(created);
        } catch {
          // Non-fatal: skip tags that fail to create
        }
      }
    }

    if (toApply.length > 0) {
      setAvailableTags(prev => {
        const existing = new Set(prev.map(t => t.id));
        return [...prev, ...toApply.filter(t => !existing.has(t.id))];
      });
      setValues(prev => ({
        ...prev,
        tagIds: [
          ...prev.tagIds,
          ...toApply.map(t => t.id).filter(id => !prev.tagIds.includes(id)),
        ],
      }));
    }

    setAiSuggestions(prev => ({ ...prev, tags: [] }));
  };

  const handleDismissTags = () => {
    setAiSuggestions(prev => ({ ...prev, tags: [] }));
  };

  // ── Disabled states ────────────────────────────────────────────────────────
  const titleApplyDisabled = !aiSuggestions.title || aiSuggestions.title === values.title;
  const descApplyDisabled =
    !aiSuggestions.description || aiSuggestions.description === values.description;
  const newApplicableTags = aiSuggestions.tags.filter(name => {
    const existing = availableTags.find(t => t.name.toLowerCase() === name.toLowerCase());
    return !existing || !values.tagIds.includes(existing.id);
  });
  const tagsApplyDisabled = newApplicableTags.length === 0;

  // -------------------------------------------------------------------------
  // Status option metadata
  // -------------------------------------------------------------------------

  const STATUS_OPTION_META: Record<
    'draft' | 'private' | 'public' | 'archived',
    { icon: React.ReactNode; label: string; caption: string }
  > = {
    draft: {
      icon: <DraftIcon fontSize="small" />,
      label: 'Draft',
      caption: 'Only visible to you and Team Admins',
    },
    private: {
      icon: <PrivateIcon fontSize="small" />,
      label: 'Private',
      caption: 'Visible to team members with read permission',
    },
    public: {
      icon: <PublicIcon fontSize="small" />,
      label: 'Public',
      caption: 'Shareable via public links',
    },
    archived: {
      icon: <ArchiveIcon fontSize="small" />,
      label: 'Archived',
      caption: 'Read-only, only visible to you and Team Admins',
    },
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const titleValue = values.title ?? '';
  const descriptionValue = values.description ?? '';
  const showAi = !!contentId;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: '80vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            <Typography variant="h6">Edit metadata</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* General error */}
          {errors.general && (
            <Alert
              severity="error"
              onClose={() => setErrors(prev => ({ ...prev, general: '' }))}
            >
              {errors.general}
            </Alert>
          )}

          {/* Basic information */}
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
              Basic Information
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <TextField
                  label="Title"
                  value={titleValue}
                  onChange={handleChange('title')}
                  error={!!errors.title}
                  helperText={errors.title || 'A clear, descriptive title'}
                  fullWidth
                  required
                  autoFocus
                />
                {/* AI title suggestion row */}
                {showAi && !regenerating && aiSuggestions.title && aiSuggestions.title !== titleValue && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, px: 0.5 }}>
                    <AutoAwesomeIcon sx={{ fontSize: 13, color: 'primary.main', flexShrink: 0 }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {aiSuggestions.title}
                    </Typography>
                    <Button
                      size="small"
                      disabled={titleApplyDisabled}
                      sx={{ py: 0, minWidth: 52, fontSize: '0.7rem' }}
                      onClick={handleApplyTitle}
                    >
                      Apply
                    </Button>
                    <Button
                      size="small"
                      sx={{ py: 0, minWidth: 52, fontSize: '0.7rem' }}
                      onClick={handleDismissTitle}
                    >
                      Dismiss
                    </Button>
                  </Box>
                )}
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Description *
                </Typography>
                <WYSIWYGRichTextEditor
                  value={descriptionValue}
                  onChange={handleDescriptionChange}
                  placeholder="Describe the content and its purpose"
                  minHeight={160}
                  borderWidth={1}
                />
                {errors.description && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.description}
                  </Typography>
                )}
                {/* AI description suggestion row */}
                {showAi && !regenerating && aiSuggestions.description && aiSuggestions.description !== descriptionValue && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5, px: 0.5 }}>
                    <AutoAwesomeIcon sx={{ fontSize: 13, color: 'primary.main', flexShrink: 0, mt: 0.25 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={
                          descExpanded
                            ? { whiteSpace: 'pre-wrap', display: 'block' }
                            : {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                              }
                        }
                      >
                        {aiSuggestions.description}
                      </Typography>
                      <Button
                        size="small"
                        sx={{ p: 0, minWidth: 0, fontSize: '0.7rem', textTransform: 'none' }}
                        onClick={() => setDescExpanded(e => !e)}
                      >
                        {descExpanded ? '[less]' : '[more]'}
                      </Button>
                    </Box>
                    <Button
                      size="small"
                      disabled={descApplyDisabled}
                      sx={{ py: 0, minWidth: 52, fontSize: '0.7rem', flexShrink: 0 }}
                      onClick={handleApplyDescription}
                    >
                      Apply
                    </Button>
                    <Button
                      size="small"
                      sx={{ py: 0, minWidth: 52, fontSize: '0.7rem', flexShrink: 0 }}
                      onClick={handleDismissDescription}
                    >
                      Dismiss
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Tags */}
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
              Tags
            </Typography>

            {values.teamId && values.organizationId ? (
              <>
                <TagAutoComplete
                  appliedTagIds={values.tagIds ?? []}
                  availableTags={availableTags}
                  loading={loadingTags}
                  onTagApply={handleTagApply}
                  onTagRemove={handleTagRemove}
                  onCreateTag={handleCreateTag}
                  tagScope="team"
                  organizationId={values.organizationId}
                  teamId={values.teamId}
                  userId=""
                  placeholder="Type to add tags..."
                  label="Tags"
                  maxTags={10}
                />
                {/* AI tags suggestion row */}
                {showAi && !regenerating && aiSuggestions.tags.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 0.75,
                      px: 0.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: 13, color: 'primary.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                      {aiSuggestions.tags.map((name, i) => {
                        const existing = availableTags.find(
                          t => t.name.toLowerCase() === name.toLowerCase(),
                        );
                        const alreadyApplied = existing && values.tagIds.includes(existing.id);
                        return (
                          <Box
                            key={i}
                            component="span"
                            sx={{ color: alreadyApplied ? 'text.disabled' : 'text.secondary' }}
                          >
                            {i > 0 ? ', ' : ''}
                            {name}
                          </Box>
                        );
                      })}
                    </Typography>
                    <Button
                      size="small"
                      disabled={tagsApplyDisabled}
                      sx={{ py: 0, minWidth: 72, fontSize: '0.7rem', flexShrink: 0 }}
                      onClick={handleApplyTags}
                    >
                      Apply Tags
                    </Button>
                    <Button
                      size="small"
                      sx={{ py: 0, minWidth: 52, fontSize: '0.7rem', flexShrink: 0 }}
                      onClick={handleDismissTags}
                    >
                      Dismiss
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              <Alert severity="info">
                Tags will be available once the item has team and organization context.
              </Alert>
            )}
            {errors.tagIds && (
              <FormHelperText error>{errors.tagIds}</FormHelperText>
            )}
          </Box>

          <Divider />

          {/* Status */}
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
              Status
            </Typography>

            <FormControl fullWidth>
              <InputLabel id="content-status-label">Status</InputLabel>
              <Select
                labelId="content-status-label"
                value={values.status ?? 'private'}
                onChange={e => {
                  const newStatus = e.target.value as
                    | 'draft'
                    | 'private'
                    | 'public'
                    | 'archived';
                  setValues(prev => ({
                    ...prev,
                    status: newStatus,
                    isPublic: newStatus === 'public',
                  }));
                }}
                label="Status"
              >
                {availableStatuses.map(statusKey => {
                  const { icon, label, caption } = STATUS_OPTION_META[statusKey];
                  return (
                    <MenuItem key={statusKey} value={statusKey}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {icon}
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {caption}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
              <FormHelperText>
                Choose who can access this item based on its status
              </FormHelperText>
            </FormControl>
          </Box>

          {/* AI Enrichment section — only when contentId provided */}
          {showAi && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                  AI Enrichment
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Regenerate AI-suggested title, description, and tags using the current content.
                  Your edits above will be saved first so enrichment uses your latest metadata.
                </Typography>

                {regenerateError && (
                  <Alert severity="error" sx={{ mt: 1, mb: 1 }} onClose={() => setRegenerateError(null)}>
                    {regenerateError}
                  </Alert>
                )}
                {regenerateSuccess && !regenerating && (
                  <Alert severity="success" sx={{ mt: 1, mb: 1 }} onClose={() => setRegenerateSuccess(false)}>
                    AI enrichment complete. Review suggestions above and click Apply to adopt them.
                  </Alert>
                )}
                {pollTimedOut && (
                  <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
                    Processing is taking longer than expected. Close this dialog and return later — the Content page will notify you when enrichment completes.
                  </Alert>
                )}
                {regenerating && (
                  <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mt: 1, mb: 1 }}>
                    AI enrichment is in progress. Your form is preserved — suggestions will appear above once generation completes. You can close this dialog and return later.
                  </Alert>
                )}

                <Box sx={{ mt: 1.5 }}>
                  <Button
                    variant="outlined"
                    startIcon={regenerating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                    disabled={regenerating || isSaving || !titleValue.trim()}
                    onClick={handleRegenerateEnrichment}
                  >
                    {regenerating ? 'Generating…' : 'Regenerate AI Enrichment'}
                  </Button>
                </Box>
              </Box>

              {/* Thumbnail — deliberately separate from AI Enrichment above.
                  That action resets the AI auto-promotion settings so fresh
                  suggestions can be reviewed; this one must not, because
                  someone fixing a missing preview image did not ask for it.

                  Gated on kind, not on showAi: a View has no file and so has no
                  thumbnail to regenerate. Offering it there sent the request
                  down the view branch of retrySingleContent, which runs full AI
                  enrichment — rewriting the title and description of an item the
                  user only wanted a preview image for. The service refuses it
                  now too; this just stops offering something that cannot work. */}
              {kind === 'file' && (
              <>
              <Divider />
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                  Thumbnail
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Regenerate the preview image for this item. Your AI settings and
                  suggestions are left untouched.
                </Typography>

                {thumbnailError && (
                  <Alert severity="error" sx={{ mt: 1, mb: 1 }} onClose={() => setThumbnailError(null)}>
                    {thumbnailError}
                  </Alert>
                )}
                {thumbnailQueued && !regeneratingThumbnail && (
                  <Alert severity="success" sx={{ mt: 1, mb: 1 }} onClose={() => setThumbnailQueued(false)}>
                    Thumbnail regenerated. Refresh the Content page to see it.
                  </Alert>
                )}

                <Box sx={{ mt: 1.5 }}>
                  <Button
                    variant="outlined"
                    startIcon={regeneratingThumbnail ? <CircularProgress size={16} /> : <ImageIcon />}
                    disabled={regeneratingThumbnail || isSaving}
                    onClick={handleRegenerateThumbnail}
                  >
                    {regeneratingThumbnail ? 'Regenerating…' : 'Regenerate Thumbnail'}
                  </Button>
                </Box>
              </Box>
              </>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} color="inherit" disabled={isSaving}>
          {regenerating ? 'Close (keep generating)' : 'Cancel'}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={!titleValue.trim() || !descriptionValue.trim() || isSaving || regenerating}
          startIcon={isSaving ? <CircularProgress size={20} /> : undefined}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ContentMetadataDialog;
